import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'

const devTenant = import.meta.env.VITE_DEV_TENANT

export interface AuthedApi<T extends { token: string }> {
  api: AxiosInstance
  getSession: () => T | null
  setSession: (s: T) => void
  clearSession: () => void
  hasSessionHint: () => boolean
  setUnauthorizedHandler: (handler: (() => void) | null) => void
  bootstrapSession: () => Promise<T | null>
  logoutServerSession: () => Promise<void>
}

/**
 * Builds one realm's API client + in-memory session store (D-053). The customer and staff
 * realms each get their OWN instance — separate axios client, separate session, separate
 * refresh cookie endpoints — so the two principals never mix (token separation, §9 / D-027).
 *
 * Session model: the short-lived access token is held in MEMORY only, never localStorage —
 * the long login lives in the realm's httpOnly refresh cookie. A tiny localStorage hint flag
 * (no token in it) remembers that a login existed so guests never fire the doomed boot
 * refresh. The store lives outside React so the axios interceptor can read the token
 * synchronously on every request.
 *
 * The client uses a relative `/api` base — the Vite dev proxy and the prod single-image jar
 * both serve it same-origin. In dev the tenant is sent via the X-Tenant header; in production
 * the backend resolves it from the subdomain, so no header is attached.
 */
export function createAuthedApi<T extends { token: string }>(opts: {
  /** localStorage hint flag (never a token) — records that a login existed. */
  hintKey: string
  /** Pre-D-053 localStorage session key (held a raw JWT) — cleared whenever we touch storage. */
  legacyKey: string
  /** Cookie-authenticated rotate endpoint, e.g. `/api/auth/refresh`. */
  refreshPath: string
  /** Cookie-authenticated family-revoke endpoint, e.g. `/api/auth/logout`. */
  logoutPath: string
}): AuthedApi<T> {
  const { hintKey, legacyKey, refreshPath, logoutPath } = opts

  let session: T | null = null

  const getSession = () => session

  const setSession = (s: T) => {
    session = s
    try {
      localStorage.setItem(hintKey, '1')
      localStorage.removeItem(legacyKey)
    } catch {
      // localStorage unavailable (private mode) — the session just won't survive a reload hint-less.
    }
  }

  const clearSession = () => {
    session = null
    try {
      localStorage.removeItem(hintKey)
      localStorage.removeItem(legacyKey)
    } catch {
      // ignore
    }
  }

  /** True when a previous login should be restorable — worth one silent refresh at boot. */
  const hasSessionHint = () => {
    try {
      // A legacy stored session also counts: its holder gets one refresh attempt (which fails
      // without a cookie and cleans up), so pre-D-053 users just re-login once.
      return localStorage.getItem(hintKey) === '1' || localStorage.getItem(legacyKey) !== null
    } catch {
      return false
    }
  }

  const api = axios.create({ baseURL: '/api' })

  api.interceptors.request.use((config) => {
    if (devTenant) {
      config.headers.set('X-Tenant', devTenant)
    }
    if (session) {
      config.headers.set('Authorization', `Bearer ${session.token}`)
    }
    return config
  })

  /**
   * Called when a request 401s and the silent refresh could not rescue it — i.e. the long
   * session is gone (expired, revoked, logged out elsewhere). Registered by the realm's auth
   * context so it clears the React session too (the D-047 "401 → re-login" line).
   */
  let onUnauthorized: (() => void) | null = null

  const setUnauthorizedHandler = (handler: (() => void) | null) => {
    onUnauthorized = handler
  }

  /**
   * Silent refresh, single-flight: concurrent 401s share one `/refresh` call — mandatory with
   * rotation, or parallel requests would race the cookie and trip reuse detection. Uses BARE
   * axios, not `api`: the refresh request must not carry a bearer (the resource server rejects
   * a present-but-expired token even on permitAll endpoints) and must not recurse through the
   * interceptors. The realm's httpOnly refresh cookie rides along automatically.
   */
  let refreshPromise: Promise<T> | null = null

  const refreshSession = (): Promise<T> => {
    if (!refreshPromise) {
      refreshPromise = axios
        .post<T>(refreshPath, null, {
          headers: devTenant ? { 'X-Tenant': devTenant } : undefined,
        })
        .then(({ data }) => {
          setSession(data)
          return data
        })
        .finally(() => {
          refreshPromise = null
        })
    }
    return refreshPromise
  }

  /**
   * One boot-time silent refresh: restores the login after a page reload (this is what makes
   * "stay logged in for days" work with an in-memory access token). Resolves null when there
   * was no session to restore (no hint — no doomed network call) and for dead sessions
   * (which it cleans up).
   */
  const bootstrapSession = async (): Promise<T | null> => {
    if (!hasSessionHint()) {
      return null
    }
    try {
      return await refreshSession()
    } catch (error) {
      // Only a real server rejection means the session is dead. A network hiccup (offline
      // shopkeeper phone) keeps the hint so the next load can try again.
      if (axios.isAxiosError(error) && error.response) {
        clearSession()
      }
      return null
    }
  }

  /** Server-side logout: revokes the refresh-token family + clears the cookie. Cookie-only, no bearer. */
  const logoutServerSession = async (): Promise<void> => {
    await axios.post(logoutPath, null, {
      headers: devTenant ? { 'X-Tenant': devTenant } : undefined,
    })
  }

  api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined
      // Only act when we actually sent a token, so a bad-password 401 on the login page (no
      // session) and public guest calls fall through to their own handling.
      if (error.response?.status === 401 && original && !original._retried && session) {
        original._retried = true
        try {
          await refreshSession()
          // Re-dispatch through `api` so the request interceptor attaches the fresh token.
          return await api(original)
        } catch {
          clearSession()
          onUnauthorized?.()
        }
      }
      return Promise.reject(error)
    },
  )

  return {
    api,
    getSession,
    setSession,
    clearSession,
    hasSessionHint,
    setUnauthorizedHandler,
    bootstrapSession,
    logoutServerSession,
  }
}
